import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { useIntl } from 'react-intl';
import ImageColumGrid from '../components/common/ImageColumGrid';
import { AppContext } from '../contexts/AppContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalPizza from '@mui/icons-material/LocalPizza';
import Snackbar from '@mui/material/Snackbar';
import posthog from 'posthog-js';
import useRestricted from '../hooks/useRestricted';
import { Permission } from '../services/UserService';
import LoadingButton from '@mui/lab/LoadingButton';
import { TrainingRun } from '../services/TrainingRunsService';
import { Plan, NutritionProductPlan } from '../services/PlansService';
import TrainingRunStep1Source from '../components/trainingRuns/TrainingRunStep1Source';
import TrainingRunStep2SelectPlan from '../components/trainingRuns/TrainingRunStep2SelectPlan';
import TrainingRunStep3Details from '../components/trainingRuns/TrainingRunStep3Details';
import TrainingRunStepStravaConnect from '../components/trainingRuns/TrainingRunStepStravaConnect';
import TrainingRunStepStravaSelect from '../components/trainingRuns/TrainingRunStepStravaSelect';
import dayjs from 'dayjs';

const AddTrainingRun: React.FC = observer(() => {
  const permission = useRestricted();

  const intl = useIntl();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (permission === Permission.free) {
      navigate('/');
    }
  }, [permission, navigate]);
  const [searchParams] = useSearchParams();
  const stravaCode = searchParams.get('code');
  const stravaError = searchParams.get('error');

  const { userService, plansService, trainingRunsService, stravaService } = React.useContext(AppContext);

  const [activeStep, setActiveStep] = React.useState<number>(0);
  const [selectedSource, setSelectedSource] = React.useState<"timeontrails" | "strava" | "manual" | null>(null);
  const [selectedPlan, setSelectedPlan] = React.useState<Plan | null>(null);
  const [selectedDate, setSelectedDate] = React.useState<dayjs.Dayjs | null>(null);
  const [selectedActivityId, setSelectedActivityId] = React.useState<number | null>(null);
  const [trainingRunDraft, setTrainingRunDraft] = React.useState<Partial<TrainingRun> | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (userService.user?.id && selectedSource === "timeontrails") {
      plansService.loadPlans(userService.user.id, true);
    }
  }, [userService.user?.id, selectedSource, plansService]);

  React.useEffect(() => {
    async function handleCallback() {
      setError(null);

      if (!stravaCode && !stravaError) return;

      if (stravaError) {
        setError("error.stravaAuthDeniedOrCancelled");
        return;
      }

      if (stravaCode) {
        setIsLoading(true);

        setTimeout(async () => {
          try {
            const success = await stravaService.handleCallback(stravaCode);
            if (success) {
              setError(null);
            } else {
              setError("error.stravaFailedConnect");
            }
          } catch (err) {
            setError("error.stravaUnexpectedError");
          } finally {
            setIsLoading(false);
          }
        }, 1000);
      }
    }

    if (!stravaService.isConnected && (stravaCode || stravaError)) {
      handleCallback();
    }
  }, [stravaCode, stravaError, stravaService]);

  React.useEffect(() => {
    if (stravaService.isConnected && !isLoading && selectedSource === 'strava') {
      if (activeStep === 1) {
        setActiveStep(2);
      }

      // Initialize date to today when reaching step 2
      if (activeStep === 2 && !selectedDate) {
        setSelectedDate(dayjs());
      }

      if (selectedDate) {
        const startOfDay = new Date(selectedDate.toDate());
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(selectedDate.toDate());
        endOfDay.setHours(23, 59, 59, 999);

        stravaService.getActivitiesByDateRange(startOfDay, endOfDay);
      }
    }
  }, [stravaService.isConnected, activeStep, selectedDate, isLoading, selectedSource, stravaService]);

  const handleBack = () => {
    if (activeStep > 0) {
      // For Strava step 2, go back to source selection
      if (selectedSource === 'strava' && activeStep === 2) {
        setSelectedActivityId(null);
        setSelectedDate(null);
        setTrainingRunDraft(null);
        setActiveStep(0);
      } else {
        setActiveStep(activeStep - 1);
      }
    } else {
      handleCancel();
    }
  };

  const handleNext = () => {
    setActiveStep(activeStep + 1);
  };

  const handleCancel = () => {
    navigate('/nutritional-training');
  };

  const handleSelectSource = (source: "timeontrails" | "strava" | "manual") => {
    posthog.capture('training_run_source_selected', { source });
    setSelectedSource(source);

    if (source === 'manual') {
      // Create empty draft for manual entry
      const draft: Partial<TrainingRun> = {
        name: '',
        date: new Date(),
        distance: 0,
        elevationGain: 0,
        elevationLoss: 0,
        time: 0,
        nutrition: [],
        performance: 5,
        gutIssues: 5,
        temperature: 5,
        notes: '',
        source: 'manual',
        userId: userService.user?.id || '',
      };
      setTrainingRunDraft(draft);
    }

    handleNext();
  };

  const handleDateChange = (date: dayjs.Dayjs | null) => {
    setSelectedDate(date);
    setSelectedActivityId(null);

    if (date) {
      const startOfDay = new Date(date.toDate());
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(date.toDate());
      endOfDay.setHours(23, 59, 59, 999);

      stravaService.getActivitiesByDateRange(startOfDay, endOfDay);
    }
  };

  const handleSelectActivity = (activityId: number) => {
    setSelectedActivityId(activityId);

    // Map Strava activity to training run draft
    const activity = stravaService.activities.find(a => a.id === activityId);
    if (activity) {
      const draft: Partial<TrainingRun> = {
        name: activity.name,
        date: new Date(activity.start_date),
        distance: activity.distance / 1000, // Convert meters to km
        elevationGain: activity.total_elevation_gain || 0,
        elevationLoss: -1 * (activity.total_elevation_gain || 0),
        time: Math.round(activity.elapsed_time / 60), // Convert seconds to minutes and round
        nutrition: [],
        performance: 5,
        gutIssues: 5,
        temperature: 5,
        notes: '',
        source: 'strava',
        userId: userService.user?.id || '',
      };
      setTrainingRunDraft(draft);
    }
  };

  const handleSelectPlan = (plan: Plan) => {
    posthog.capture('training_run_plan_selected', { planId: plan.id });
    setSelectedPlan(plan);

    // Calculate total time from planned effort
    const totalTime = plan.plannedEffort.reduce((acc, effort) => acc + effort.time, 0);

    // Get nutrition from all sections
    const allNutrition = plan.plannedNutrition.reduce<NutritionProductPlan[]>((acc, section) => {
      if (section.products) {
        section.products.forEach(product => {
          // Skip invalid or empty products
          if (!product.productId || product.productId === 'empty' || product.productId.trim() === '') {
            return;
          }
          const existing = acc.find(p => p.productId === product.productId);
          if (existing) {
            existing.quantity += product.quantity;
          } else {
            acc.push({ ...product });
          }
        });
      }
      return acc;
    }, []).filter(p => p.productId && p.productId !== 'empty' && p.productId.trim() !== '');

    // Create draft from plan data
    const draft: Partial<TrainingRun> = {
      name: plan.name,
      date: plan.date,
      distance: plan.distance,
      elevationGain: plan.elevationGain,
      elevationLoss: plan.elevationLoss,
      time: totalTime,
      nutrition: allNutrition || [],
      performance: 5,
      gutIssues: 5,
      temperature: 5,
      notes: '',
      source: 'timeontrails',
      userId: userService.user?.id || '',
    };

    setTrainingRunDraft(draft);
    handleNext();
  };

  const handleSubmit = async () => {
    if (!trainingRunDraft || !userService.user?.id) {
      setError('error.missingRequiredData');
      return;
    }

    setIsLoading(true);

    try {
      const newTrainingRun: TrainingRun = {
        id: `${userService.user.id}_${Date.now()}`,
        name: trainingRunDraft.name || '',
        date: trainingRunDraft.date || new Date(),
        distance: trainingRunDraft.distance || 0,
        time: trainingRunDraft.time || 0,
        elevationGain: trainingRunDraft.elevationGain || 0,
        elevationLoss: trainingRunDraft.elevationLoss || 0,
        nutrition: trainingRunDraft.nutrition || [],
        performance: trainingRunDraft.performance || 5,
        gutIssues: trainingRunDraft.gutIssues || 5,
        temperature: trainingRunDraft.temperature || 5,
        notes: trainingRunDraft.notes || '',
        source: trainingRunDraft.source || 'manual',
        userId: userService.user.id,
        createdAt: new Date(),
        modifiedAt: new Date(),
      };

      await trainingRunsService.saveTrainingRun(newTrainingRun);

      posthog.capture('training_run_created', {
        training_run_id: newTrainingRun.id,
        source: newTrainingRun.source
      });

      navigate('/nutritional-training');

    } catch (error) {
      console.error("Error saving training run:", error);
      setError('error.savingTrainingRun');
    } finally {
      setIsLoading(false);
    }
  };

  const steps = [
    intl.formatMessage({ id: 'trainingRun.steps.selectSource' }),
    selectedSource === 'timeontrails' ? intl.formatMessage({ id: 'trainingRun.steps.selectPlan' }) :
    selectedSource === 'strava' ? intl.formatMessage({ id: 'comparison.steps.connect' }) : '',
    selectedSource === 'strava' ? intl.formatMessage({ id: 'comparison.steps.select' }) : '',
    intl.formatMessage({ id: 'trainingRun.steps.details' }),
  ].filter(Boolean);

  const getPrimaryButtonProps = () => {
    if (activeStep === steps.length - 1) {
      return {
        onClick: handleSubmit,
        disabled: !trainingRunDraft || !trainingRunDraft.name || trainingRunDraft.name.trim() === '' || isLoading,
        label: intl.formatMessage({ id: 'action.save' })
      };
    } else {
      return {
        onClick: handleNext,
        disabled:
          (activeStep === 0 && !selectedSource) ||
          (activeStep === 1 && selectedSource === 'timeontrails' && !selectedPlan) ||
          (activeStep === 1 && selectedSource === 'strava' && !stravaService.isConnected) ||
          (activeStep === 2 && selectedSource === 'strava' && !selectedActivityId) ||
          isLoading,
        label: intl.formatMessage({ id: 'nav.next' })
      };
    }
  };

  const primaryButtonProps = getPrimaryButtonProps();

  const mainComponent = <Box
    sx={{
      my: 8,
      mx: 4,
      display: 'flex',
      flexDirection: 'column',
      textAlign: 'center',
    }}
  >
    <Button startIcon={<ArrowBackIcon />} sx={{ alignSelf: "flex-start" }}
      onClick={handleBack}
      color="inherit"
    >
      {intl.formatMessage({ id: 'action.back' })}
    </Button>

    <Avatar sx={{ marginX: "auto", marginY: 1, bgcolor: 'secondary.main' }}>
      <LocalPizza />
    </Avatar>
    <Typography component="h1" variant="h5" gutterBottom>
      {activeStep === 0 && intl.formatMessage({ id: 'trainingRun.chooseSource' })}
      {activeStep === 1 && selectedSource === 'timeontrails' && intl.formatMessage({ id: 'trainingRun.selectPlan' })}
      {activeStep === 1 && selectedSource === 'strava' && intl.formatMessage({ id: 'trainingRun.connectStrava' })}
      {activeStep === 2 && selectedSource === 'strava' && intl.formatMessage({ id: 'trainingRun.selectActivity' })}
      {((activeStep === 1 && selectedSource === 'manual') || (activeStep === 2 && selectedSource === 'timeontrails') || (activeStep === 3 && selectedSource === 'strava')) && intl.formatMessage({ id: 'trainingRun.addTitle' })}
    </Typography>

    {activeStep === 0 && (
      <TrainingRunStep1Source onSelectSource={handleSelectSource} />
    )}

    {activeStep === 1 && selectedSource === 'timeontrails' && (
      <TrainingRunStep2SelectPlan
        plans={plansService.plans}
        isLoading={plansService.isLoadingPlans}
        onSelectPlan={handleSelectPlan}
      />
    )}

    {activeStep === 1 && selectedSource === 'strava' && (
      <TrainingRunStepStravaConnect
        isLoading={isLoading}
        error={error}
        onConnect={() => stravaService.connect()}
      />
    )}

    {activeStep === 2 && selectedSource === 'strava' && (
      <TrainingRunStepStravaSelect
        activities={stravaService.activities}
        isLoading={stravaService.isLoading || isLoading}
        selectedDate={selectedDate}
        selectedActivityId={selectedActivityId}
        onDateChange={handleDateChange}
        onSelectActivity={handleSelectActivity}
        error={error}
      />
    )}

    {((activeStep === 1 && selectedSource === 'manual') || (activeStep === 2 && selectedSource === 'timeontrails') || (activeStep === 3 && selectedSource === 'strava')) && trainingRunDraft && (
      <TrainingRunStep3Details
        trainingRun={trainingRunDraft}
        onUpdate={setTrainingRunDraft}
      />
    )}

    {activeStep !== 0 && !(activeStep === 1 && selectedSource === 'timeontrails') && (
      <LoadingButton
        loading={isLoading}
        fullWidth
        variant="contained"
        onClick={primaryButtonProps.onClick}
        disabled={primaryButtonProps.disabled}
        sx={{ my: 1 }}
      >
        {primaryButtonProps.label}
      </LoadingButton>
    )}
  </Box>

  return (
    <>
      {activeStep === 1 && selectedSource === 'timeontrails' ? (
        mainComponent
      ) : (
        <ImageColumGrid image={`${process.env.PUBLIC_URL}/images/auth-background.jpg`}>
          {mainComponent}
        </ImageColumGrid>
      )}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        message={error ? intl.formatMessage({ id: error }) : ''}
      />
    </>
  );
});

export default AddTrainingRun;
